<template>
	<div class="sub_contents card_benefit_page">
		<div class="cont_center">
			<h1 class="title_page">{{ cardLabel }} 청구할인 혜택 정리</h1>

			<!-- 첫 문단: AI 검색이 답으로 그대로 가져가는 자리. 숫자·기준월은 스냅샷 생성 시점의 API 데이터로 채운다. -->
			<p class="sub_descript lead_txt">{{ leadText }}</p>
			<p v-if="baseDate" class="base_date">
				최종 업데이트 <time :datetime="isoDate">{{ baseDate }}</time>
			</p>

			<p v-if="isError" class="state_txt">데이터를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</p>
			<p v-else-if="isLoading" class="state_txt">데이터를 불러오는 중...</p>

			<template v-else>
				<!-- 빠른 이동: href 는 봇·새 탭용으로 두고, 클릭은 scrollIntoView 로 처리한다(해시 이동이 라우터 scrollBehavior 에 걸려 맨 위로 튀지 않게) -->
				<nav class="quick_nav" aria-label="빠른 이동">
					<a href="#category-summary" @click.prevent="scrollToSection('category-summary')">업종별 혜택</a>
					<a href="#billing-rule" @click.prevent="scrollToSection('billing-rule')">적용 방식</a>
					<a href="#billing-exception" @click.prevent="scrollToSection('billing-exception')">예외사항</a>
					<RouterLink to="/HFO_BI_001" class="is_primary">제휴 브랜드 보기</RouterLink>
				</nav>

				<!--
					업종별 요약만 제공한다. 브랜드 전체 표(할인율·매장 수·유의사항·적용 카드사)는 경쟁사에 한 번에 정리된 자료가 되고,
					카드사별 축은 복지카드 사이트에서 VIP 카드 프로그램(나라사랑·프라임+ 등)과 범위가 섞여 보여 두지 않는다 (2026-11-03).
					브랜드별 조건은 제휴 브랜드 목록(HFO_BI_001)과 상세(HFO_BI_002)에서 본다.
					모바일(767px 이하)에서는 표를 행 단위 카드로 다시 배치한다 (data-label 이 셀 머리글 역할).
				-->
				<section id="category-summary" class="benefit_section">
					<h2>업종별 {{ cardLabel }} 청구할인 혜택</h2>
					<p class="section_descript">
						제휴 브랜드는 {{ categoryGroups.length }}개 업종으로 나뉘며, 업종별 브랜드 수와 할인율 범위는 아래와 같습니다. 대표 브랜드는 일부 예시입니다.
					</p>
					<table class="benefit_table">
						<caption>업종별 제휴 브랜드 수와 청구할인율 ({{ baseDate }} 기준)</caption>
						<thead>
							<tr>
								<th scope="col">업종</th>
								<th scope="col">제휴 브랜드 수</th>
								<th scope="col">청구할인율</th>
								<th scope="col">대표 브랜드 (예시)</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="group in categoryGroups" :key="group.name">
								<th scope="row" class="cell_name">{{ group.name }}</th>
								<td class="cell_count" data-label="제휴 브랜드">{{ group.count }}개</td>
								<td class="cell_rate" data-label="청구할인">{{ group.range }}</td>
								<td class="cell_example" data-label="예">{{ group.exampleText }}</td>
							</tr>
						</tbody>
					</table>
					<p class="more_link">
						브랜드별 할인율·적용 카드·매장 위치는 <RouterLink to="/HFO_BI_001">제휴 브랜드 목록</RouterLink>과 각 브랜드 상세 페이지에서 확인해 주세요.
					</p>
				</section>
			</template>

			<!-- 청구할인 방식과 예외사항 (FAQ 공식 답변 기준) -->
			<section id="billing-rule" class="benefit_section">
				<h2>청구할인 적용 방식</h2>
				<ul class="rule_list">
					<li>제휴 가맹점에서 복지카드 및 기업 패밀리카드로 결제하면 익월 카드 청구 금액에서 할인됩니다. 할인 내역은 복지카드 사용명세서에서 확인합니다.</li>
					<li>전월 카드 사용 실적과 관계없이 적용되며, 하루 이용 횟수에도 제한이 없습니다.</li>
					<li>할부 결제도 적용됩니다. 카드사 매입 시 제휴 할인이 적용된 금액으로 할부 처리됩니다.</li>
					<li>가맹점 할인행사와 중복 적용됩니다. 할인행사가 적용된 금액에 제휴 할인이 다시 적용됩니다.</li>
					<li>복지포인트가 소진되어도 복지카드로 결제하면 할인됩니다.</li>
					<li>추가 가입·연회비 없이, 하이비타민 서비스가 탑재된 복지카드 및 기업 패밀리카드라면 바로 적용됩니다.</li>
				</ul>
			</section>

			<section id="billing-exception" class="benefit_section">
				<h2>청구할인 예외사항</h2>
				<ul class="rule_list">
					<li>하이비타민 서비스가 탑재된 복지카드 및 기업 패밀리카드만 대상이며, 개인 단위 신청은 할 수 없습니다.</li>
					<li>제휴 브랜드라도 승인된 가맹점에서만 적용됩니다. 백화점·할인점 내 입점 매장과 일부 가맹점은 제외될 수 있으므로 방문 전 [매장찾기]에서 확인해 주세요.</li>
					<li>카드사 청구할인 행사와는 중복 적용되지 않을 수 있습니다.</li>
					<li>삼성페이·페이코 등 간편결제로 결제하면 카드사에 따라 할인이 적용되지 않을 수 있습니다.</li>
					<li>카드 명세서나 결제 문자에 표시되는 가맹점명은 제휴사 정책에 따라 다를 수 있으며, [매장찾기]에서 확인한 제휴 지점이라면 가맹점명이 달라도 할인됩니다.</li>
					<li>브랜드별 적용 카드사와 제외 조건은 각 브랜드 상세 페이지의 안내를 따릅니다.</li>
				</ul>
			</section>
		</div>
	</div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { getAxiosDirect } from "@/lib/AxiosCall";
import { getBranchUuid } from "@/lib/Config";
import { absoluteUrl, currentProfile, setJsonLd, setPageMeta, siteOrigin, SITE_NAME } from "@/lib/Seo";

const PAGE_SIZE = 100;
/** 페이지 최초 공개일(JSON-LD datePublished). 갱신일(dateModified)은 스냅샷 생성일이다. */
const PUBLISHED_DATE = "2026-11-01";
/** 업종별 대표 브랜드 예시 수. 전체 명단은 내지 않는다. */
const EXAMPLE_COUNT = 3;

function rateRange(brands) {
	const rates = brands.map((item) => item.rate);
	const min = Math.min(...rates);
	const max = Math.max(...rates);
	return min === max ? `${max}%` : `${min}~${max}%`;
}

/** 관리자 표시 순서(order 오름차순). 대표 브랜드 예시를 고르는 기준이라 할인율·매장 수 같은 조건은 쓰지 않는다. */
function byOrder(a, b) {
	return (Number(a.order) || 0) - (Number(b.order) || 0);
}

function formatMonth(date) {
	return `${date.getFullYear()}년 ${date.getMonth() + 1}월`;
}

/** 로컬(KST) 기준 YYYY-MM-DD. toISOString 은 UTC 라 새벽 프리렌더에서 하루 전 날짜가 된다. */
function toIsoDate(date) {
	const pad = (value) => String(value).padStart(2, "0");
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function formatDate(date) {
	return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
}

/**
 * 복지카드 청구할인 혜택 정리 페이지.
 * AI 검색(네이버 AI 브리핑·구글 AI 개요·ChatGPT 등)이 "복지카드 청구할인" 질의에 답할 때 인용할 수 있도록
 * 제휴 브랜드 총 수·할인율 범위·업종별 요약·적용 방식·예외사항을 설명문과 요약 표로 둔다.
 * 데이터는 현재 호스트 지점의 브랜드 목록 API 이고, 프리렌더 스냅샷에 그대로 담긴다. 기준일은 스냅샷 생성일이다.
 * 브랜드 전체 표와 카드사별 축은 두지 않는다 (template 주석 참고).
 *
 * 호스트별 동작 (siteProfiles 의 benefitGuide):
 * - benefitGuide 파트너(kb·nhbank·samsung·woori·geps): 제목·문장의 "복지카드" 를 프로필 payWith("KB국민카드 복지카드" 등)로 바꾸고
 *   canonical 을 자기 호스트로 둬 색인되게 한다. 그 호스트 sitemap 에도 들어간다 (scripts/sites.js).
 * - 그 밖(bridgem·kbprm·narasarang): 페이지를 보여 주지 않고 noindex 를 박은 뒤 제휴 브랜드 목록으로 돌려보낸다.
 *   bridgem 은 2026-11-04 에 내렸다. 전체·업종별 브랜드 수가 경쟁사에 서비스 규모를 한눈에 주고, 홈·FAQ 와 내용이 겹친다.
 */
export default {
	name: "HFO_HI_002",
	setup() {
		const router = useRouter();
		const profile = currentProfile();
		const isGuideHost = Boolean(profile.benefitGuide);
		/** 제목·문장에 쓰는 카드 표현. bridgem 은 "복지카드", 파트너는 "KB국민카드 복지카드" 처럼 카드명을 붙인다. */
		const cardLabel = (!profile.isMain && profile.payWith) || "복지카드";

		const brands = ref([]);
		const isLoading = ref(true);
		const isError = ref(false);
		const baseDate = ref("");
		const baseMonth = ref("");
		const isoDate = ref("");

		const brandItems = computed(() =>
			brands.value
				.map((item) => ({ ...item, rate: Number(item.benefit) || 0, category: item.brandServiceTypeName || "기타" }))
				.filter((item) => item.rate > 0)
		);

		/** 업종 순서: 브랜드가 많은 업종부터. */
		const categoryGroups = computed(() => {
			const groups = {};
			brandItems.value.forEach((item) => (groups[item.category] = groups[item.category] || []).push(item));
			return Object.keys(groups)
				.sort((a, b) => groups[b].length - groups[a].length)
				.map((name) => {
					const list = groups[name];
					const examples = list.slice().sort(byOrder).slice(0, EXAMPLE_COUNT).map((item) => item.brandName);
					return {
						name,
						count: list.length,
						range: rateRange(list),
						exampleText: examples.join(", ") + (list.length > examples.length ? " 등" : ""),
					};
				});
		});

		const leadText = computed(() => {
			const mechanism = "결제 시 정상가로 승인되고 다음 달 카드 명세서에서 할인 금액만큼 차감되어 청구됩니다. 추가 가입·연회비·전월 실적 조건 없이 기존 할인과 중복 적용됩니다.";
			if (!brandItems.value.length) {
				return `하이비타민은 제휴 가맹점에서 ${cardLabel} 및 기업 패밀리카드로 결제하면 청구할인이 적용되는 서비스입니다. ` + mechanism;
			}
			const categories = categoryGroups.value.map((group) => group.name).join(", ");
			// 총 브랜드 수는 쓰지 않는다 (서비스 규모를 한눈에 주는 숫자. 업종별 수만 표에 둔다, 2026-11-04)
			return `${cardLabel} 및 기업 패밀리카드로 청구할인을 받을 수 있는 하이비타민 제휴 브랜드의 할인율은 ${rateRange(brandItems.value)}입니다. `
				+ `업종은 ${categories}입니다. ${mechanism} (${baseMonth.value} 기준)`;
		});

		function scrollToSection(id) {
			const element = document.getElementById(id);
			if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
		}

		/** 제목·설명에 기준월과 숫자를 넣는다. 검색 결과 스니펫과 AI 답변이 최신 정보로 판단하는 근거가 된다. */
		function setPageMetaWithDate() {
			if (!brandItems.value.length) return;
			// 업종별 브랜드 수까지 넣으면 160자 절단에 걸려 뒷문장이 잘린다. 업종명만 둔다.
			const categories = categoryGroups.value.map((group) => group.name).join("·");
			setPageMeta({
				title: `${cardLabel} 청구할인 혜택 정리 (${baseMonth.value} 기준)`,
				description: `${baseMonth.value} 기준 하이비타민 ${cardLabel} 청구할인 제휴 브랜드 할인율 ${rateRange(brandItems.value)}. `
					+ `${categories} 업종별 브랜드 수와 할인율, 청구할인 적용 방식과 예외사항 정리.`,
				path: "/HFO_HI_002",
				// benefitGuide 파트너는 자기 호스트로 색인한다. origin 을 주면 Seo 가 공통 페이지 규칙(noindex→bridgem)을 적용하지 않는다.
				origin: siteOrigin(),
			});
		}

		function setPageJsonLd() {
			const pageUrl = absoluteUrl("/HFO_HI_002");
			setJsonLd("card-benefit", {
				"@context": "https://schema.org",
				"@type": "WebPage",
				"@id": pageUrl + "#webpage",
				url: pageUrl,
				name: `${cardLabel} 청구할인 혜택 정리 | ` + SITE_NAME,
				description: leadText.value,
				inLanguage: "ko-KR",
				datePublished: PUBLISHED_DATE,
				dateModified: isoDate.value,
				about: categoryGroups.value.map((group) => ({
					"@type": "Offer",
					name: `${group.name} ${cardLabel} 청구할인 ${group.range}`,
					description: `${group.name} 업종 하이비타민 제휴 브랜드 ${group.count}곳에서 ${cardLabel} 및 기업 패밀리카드로 결제하면 ${group.range} 청구할인.`,
					category: "청구할인",
				})),
			});
		}

		async function loadBrands() {
			const branchUuid = getBranchUuid();
			const items = [];
			for (let page = 1; page <= 20; page++) {
				const data = await getAxiosDirect("/hvm/v1/branch/brand", {
					page, size: PAGE_SIZE, branchUuid, brandServiceType: "", brandName: "", brandType: "",
				});
				const list = (data && data.page && data.page.list) || [];
				items.push(...list);
				const total = Number(data && data.page && data.page.total) || 0;
				if (list.length < PAGE_SIZE || items.length >= total) break;
			}
			return items;
		}

		onMounted(async () => {
			if (!isGuideHost) {
				// 이 호스트에서는 제공하지 않는 페이지. 색인에서 빠지도록 noindex 를 남기고 브랜드 목록으로 보낸다.
				setPageMeta({ title: "제휴 브랜드", noindex: true, path: "/HFO_HI_002" });
				router.replace(profile.key === "kbprm" ? "/HFO_BI_001_KBPRM" : "/HFO_BI_001");
				return;
			}
			const now = new Date();
			baseDate.value = formatDate(now);
			baseMonth.value = formatMonth(now);
			isoDate.value = toIsoDate(now);
			try {
				brands.value = await loadBrands();
				setPageMetaWithDate();
				setPageJsonLd();
			} catch (error) {
				console.error("청구할인 혜택 데이터 조회 실패:", error);
				isError.value = true;
			} finally {
				isLoading.value = false;
			}
		});

		return { cardLabel, isLoading, isError, baseDate, isoDate, leadText, categoryGroups, scrollToSection };
	},
};
</script>

<style lang="scss" scoped>
.card_benefit_page {
	padding-bottom: 60px;

	.lead_txt {
		max-width: 860px;
		margin: 0 auto;
		color: #555;
		line-height: 1.7;
		word-break: keep-all;
	}

	.base_date {
		margin-top: 10px;
		font-size: 13px;
		color: #888;
		text-align: center;
	}

	.state_txt {
		padding: 40px 0;
		text-align: center;
		color: #666;
	}
}

.more_link {
	margin-top: 10px;
	font-size: 13px;
	line-height: 1.6;
	color: #666;
	word-break: keep-all;

	a {
		color: #ef7b26;
		text-decoration: underline;
	}
}

.quick_nav {
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	gap: 8px;
	margin-top: 24px;

	a {
		display: inline-block;
		padding: 7px 14px;
		border: 1px solid #ddd;
		border-radius: 999px;
		background: #fff;
		font-size: 13px;
		color: #444;
		text-decoration: none;
		white-space: nowrap;

		&:hover {
			border-color: #ef7b26;
			color: #ef7b26;
		}

		&.is_primary {
			border-color: #ef7b26;
			background: #ef7b26;
			color: #fff;
		}
	}
}

.benefit_section {
	margin-top: 40px;
	scroll-margin-top: 90px; /* 고정 헤더에 제목이 가리지 않게 */

	h2 {
		margin: 0 0 12px;
		font-size: 20px;
		font-weight: 700;
		color: #393939;
		word-break: keep-all;
	}

	.section_descript {
		margin-bottom: 12px;
		font-size: 14px;
		color: #666;
		line-height: 1.5;
		word-break: keep-all;
	}
}

.benefit_table {
	width: 100%;
	border-collapse: collapse;
	border-top: 2px solid #393939;
	font-size: 14px;
	line-height: 1.5;

	caption {
		caption-side: top;
		padding-bottom: 8px;
		text-align: left;
		font-size: 13px;
		color: #888;
	}

	th,
	td {
		padding: 10px 12px;
		border-bottom: 1px solid #e5e5e5;
		text-align: center;
		vertical-align: top;
		word-break: keep-all;
	}

	thead th {
		background: #f7f7f7;
		font-weight: 600;
		white-space: nowrap;
	}

	tbody th {
		font-weight: 600;
		white-space: nowrap;
	}

	.cell_example {
		text-align: left;
	}
}

.rule_list {
	padding-left: 20px;
	list-style: disc;

	li {
		margin-bottom: 8px;
		font-size: 14px;
		line-height: 1.6;
		color: #444;
		word-break: keep-all;
	}
}

@media (max-width: 1023px) {
	.benefit_section {
		margin-top: 32px;
		scroll-margin-top: 70px;

		h2 {
			font-size: 17px;
		}
	}

	.card_benefit_page .lead_txt {
		font-size: 14px;
		text-align: left;
	}
}

/* 폰: 표를 가로 스크롤 대신 행 단위 카드로 다시 배치한다. 좌우 5vw 여백 안에서 잘리는 내용이 없게 */
@media (max-width: 767px) {
	.quick_nav {
		justify-content: flex-start;
		gap: 6px;

		a {
			padding: 6px 12px;
			font-size: 12px;
		}
	}

	.benefit_table {
		display: block;
		border-top: 0;

		caption,
		thead {
			display: block;
		}

		thead tr,
		thead th {
			/* 머리글 행은 숨기되 스크린리더·봇에는 남긴다 */
			position: absolute;
			width: 1px;
			height: 1px;
			overflow: hidden;
			clip: rect(0 0 0 0);
			white-space: nowrap;
		}

		tbody {
			display: block;
		}

		tr {
			display: grid;
			grid-template-columns: 1fr auto;
			gap: 2px 10px;
			align-items: center;
			padding: 12px 14px;
			margin-bottom: 8px;
			border: 1px solid #e5e5e5;
			border-radius: 10px;
			background: #fff;
		}

		th,
		td {
			padding: 0;
			border: 0;
			text-align: left;
			white-space: normal;
		}

		.cell_name {
			grid-row: 1;
			grid-column: 1;
			font-size: 15px;
			color: #393939;
		}

		.cell_rate {
			grid-row: 1;
			grid-column: 2;
			padding: 3px 10px;
			border-radius: 999px;
			background: #fff3ea;
			font-weight: 700;
			color: #ef7b26;
			white-space: nowrap;

			&::before {
				content: attr(data-label) " ";
				font-weight: 500;
			}
		}

		.cell_count {
			grid-row: 2;
			grid-column: 1 / -1;
			font-size: 13px;
			color: #666;

			&::before {
				content: attr(data-label) " ";
			}
		}

		.cell_example {
			grid-row: 3;
			grid-column: 1 / -1;
			font-size: 13px;
			line-height: 1.5;
			color: #666;

			&::before {
				content: attr(data-label) ": ";
			}
		}
	}

	.more_link {
		font-size: 12px;
	}

	.rule_list {
		padding-left: 18px;

		li {
			font-size: 13px;
		}
	}
}
</style>
